import { isStorageQuotaError } from "@/gm/gm-monster-catalog-idb";
import {
  clampSpawnPerMinute,
  legacySpawnFrequencyToPerMinute,
} from "@/tower/tower-spawn-rate";

export type TowerFloorSpawnRow = {
  monsterId: string;
  count: number;
  /** คูณสเตต/HP ของแถวนี้ (รวมกับคูณพลังชั้น) ค่าเริ่มต้น 1 */
  powerMultiplier?: number;
  /** อัตราเกิด — จำนวนตัวต่อนาที (ระหว่างตัวในคิวของแถวนี้) */
  spawnPerMinute?: number;
};

export type TowerFloorScenePlacement = {
  partId: string;
  gridX: number;
  gridY: number;
  /** Per-instance depth offset from the scene editor (falls back to part definition in battle). */
  depthBias?: number;
};

export const TOWER_ARENA_COLS = 22;
export const TOWER_ARENA_ROWS = 14;

export type TowerFloorConfig = {
  floor: number;
  spawns: TowerFloorSpawnRow[];
  /** ปิด = ชั้นนี้ใช้มอนสเตอร์เริ่มต้นในเกม */
  enabled: boolean;
  updatedAt: number;
  /** คูณ HP / สเตตัสโจมตีของมอนสเตอร์ในชั้นนี้ (ค่าเริ่มต้น 1) */
  powerMultiplier?: number;
  scenePlacements?: TowerFloorScenePlacement[];
};

const DB_NAME = "mind-survival-gm";
const STORE = "kv";
const DB_VERSION = 1;
const CONFIG_KEY = "tower-floor-config/v1";

let dbPromise: Promise<IDBDatabase> | null = null;
let configsCache: Map<number, TowerFloorConfig> | null = null;
let hydratePromise: Promise<void> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("เปิด IndexedDB ไม่สำเร็จ"));
  });
  return dbPromise;
}

async function idbGet<T>(key: string): Promise<T | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const req = tx.objectStore(STORE).get(key);
    req.onsuccess = () => resolve((req.result as T | undefined) ?? null);
    req.onerror = () => reject(req.error ?? new Error("อ่าน IndexedDB ไม่สำเร็จ"));
  });
}

async function idbSet(key: string, value: unknown): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error("เขียน IndexedDB ไม่สำเร็จ"));
    tx.objectStore(STORE).put(value, key);
  });
}

function clampCount(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return 1;
  return Math.max(1, Math.floor(value));
}

function clampPowerMultiplier(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return 1;
  return Math.min(1000, Math.max(0.1, Math.round(value * 100) / 100));
}

function normalizeScenePlacement(raw: unknown): TowerFloorScenePlacement | null {
  if (!raw || typeof raw !== "object") return null;
  const p = raw as Partial<TowerFloorScenePlacement>;
  if (typeof p.partId !== "string" || !p.partId) return null;
  const gridX = Math.min(
    TOWER_ARENA_COLS - 1,
    Math.max(0, Math.floor(typeof p.gridX === "number" ? p.gridX : 0)),
  );
  const gridY = Math.min(
    TOWER_ARENA_ROWS - 1,
    Math.max(0, Math.floor(typeof p.gridY === "number" ? p.gridY : 0)),
  );
  return { partId: p.partId, gridX, gridY };
}

function normalizeRow(raw: unknown): TowerFloorSpawnRow | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Partial<TowerFloorSpawnRow> & { spawnFrequency?: number };
  if (typeof r.monsterId !== "string" || !r.monsterId) return null;
  const row: TowerFloorSpawnRow = {
    monsterId: r.monsterId,
    count: clampCount(r.count),
  };
  if (r.powerMultiplier != null) {
    row.powerMultiplier = clampPowerMultiplier(r.powerMultiplier);
  }
  if (r.spawnPerMinute != null) {
    row.spawnPerMinute = clampSpawnPerMinute(r.spawnPerMinute);
  } else if (r.spawnFrequency != null) {
    row.spawnPerMinute = legacySpawnFrequencyToPerMinute(r.spawnFrequency);
  }
  return row;
}

function normalizeConfig(raw: unknown): TowerFloorConfig | null {
  if (!raw || typeof raw !== "object") return null;
  const c = raw as Partial<TowerFloorConfig>;
  if (typeof c.floor !== "number" || !Number.isFinite(c.floor)) return null;
  const floor = Math.min(100, Math.max(1, Math.floor(c.floor)));
  const spawns = (Array.isArray(c.spawns) ? c.spawns : [])
    .map(normalizeRow)
    .filter((row): row is TowerFloorSpawnRow => row !== null);
  const scenePlacements = (Array.isArray(c.scenePlacements) ? c.scenePlacements : [])
    .map(normalizeScenePlacement)
    .filter((row): row is TowerFloorScenePlacement => row !== null);

  return {
    floor,
    spawns,
    enabled: c.enabled !== false,
    updatedAt: typeof c.updatedAt === "number" ? c.updatedAt : Date.now(),
    powerMultiplier: clampPowerMultiplier(c.powerMultiplier ?? 1),
    scenePlacements,
  };
}

export function isActiveTowerFloorConfig(config: TowerFloorConfig | null | undefined): boolean {
  return Boolean(config?.enabled && config.spawns.length > 0);
}

/** Block play when GM enabled a floor but spawn list is still empty. */
export function towerFloorPlayBlockReason(floor: number): string | null {
  const config = getTowerFloorConfig(floor);
  if (!config?.enabled) return null;
  if (isActiveTowerFloorConfig(config)) return null;
  return "ชั้นนี้เปิดใช้งานแล้วแต่ยังไม่มีมอนสเตอร์ — เพิ่มอย่างน้อย 1 รายการใน GM ก่อนเล่น";
}

export async function hydrateTowerFloorConfigs(): Promise<void> {
  if (configsCache) return;
  if (hydratePromise) {
    await hydratePromise;
    return;
  }
  hydratePromise = (async () => {
    const raw = await idbGet<unknown[]>(CONFIG_KEY);
    const map = new Map<number, TowerFloorConfig>();
    if (Array.isArray(raw)) {
      for (const item of raw) {
        const cfg = normalizeConfig(item);
        if (cfg) map.set(cfg.floor, cfg);
      }
    }
    configsCache = map;
  })();
  await hydratePromise;
}

export function getTowerFloorConfig(floor: number): TowerFloorConfig | null {
  const f = Math.min(100, Math.max(1, Math.floor(floor)));
  return configsCache?.get(f) ?? null;
}

export function listTowerFloorConfigs(): TowerFloorConfig[] {
  if (!configsCache) return [];
  return [...configsCache.values()].sort((a, b) => a.floor - b.floor);
}

export async function saveTowerFloorConfig(config: TowerFloorConfig): Promise<void> {
  await hydrateTowerFloorConfigs();
  const next = normalizeConfig(config);
  if (!next) throw new Error("invalid tower floor config");
  const map = new Map(configsCache ?? []);
  map.set(next.floor, next);
  const payload = [...map.values()];
  await idbSet(CONFIG_KEY, payload);
  configsCache = map;
}

export async function setTowerFloorConfigEnabled(floor: number, enabled: boolean): Promise<void> {
  await hydrateTowerFloorConfigs();
  const f = Math.min(100, Math.max(1, Math.floor(floor)));
  const existing = configsCache?.get(f);
  if (!existing) return;
  await saveTowerFloorConfig({ ...existing, enabled, updatedAt: Date.now() });
}

export async function clearTowerFloorConfig(floor: number): Promise<void> {
  await hydrateTowerFloorConfigs();
  const f = Math.min(100, Math.max(1, Math.floor(floor)));
  const map = new Map(configsCache ?? []);
  map.delete(f);
  await idbSet(CONFIG_KEY, [...map.values()]);
  configsCache = map;
}

export { isStorageQuotaError };
